import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import Sparkline from '../components/servers/Sparkline';
import {
  ChartCard,
  InfoRow,
  MetricCard,
  ServiceRow,
  StatusBadge,
} from '../components/servers/ServerWidgets';
import { apiGetServerMetrics, apiGetServers } from '../api/client';

const MAX_HISTORY = 30;
const POLL_MS = 10000;

function pushHistory(prev, key, value) {
  const arr = [...(prev[key] || []), value];
  if (arr.length > MAX_HISTORY) arr.shift();
  return { ...prev, [key]: arr };
}

function fmtUptime(ms) {
  if (!ms) return '';
  const s = Math.floor((Date.now() - ms) / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function ServerDetailPage() {
  const { serverId } = useParams();
  const [serverMeta, setServerMeta] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('online');
  const [history, setHistory] = useState({});

  const load = useCallback(async () => {
    try {
      setError('');
      const data = await apiGetServerMetrics(serverId);
      setMetrics(data);
      setHistory((prev) => {
        let next = prev;
        next = pushHistory(next, 'cpu', data.cpu?.percent ?? 0);
        next = pushHistory(next, 'mem', data.memory?.percent ?? 0);
        next = pushHistory(next, 'disk', data.disk?.root?.percent ?? 0);
        next = pushHistory(next, 'tmp', data.disk?.tmp?.percent ?? data.disk?.tmp?.dir_gb ?? 0);
        next = pushHistory(next, 'bwIn', data.network?.recv_mb_s ?? 0);
        next = pushHistory(next, 'bwOut', data.network?.sent_mb_s ?? 0);
        return next;
      });
    } catch (e) {
      setError(e.message || 'Failed to load metrics');
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }, [serverId]);

  useEffect(() => {
    apiGetServers()
      .then((list) => setServerMeta(list.find((s) => s.id === serverId) || null))
      .catch(() => {});
  }, [serverId]);

  useEffect(() => {
    setLoading(true);
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const onlinePm2 = (metrics?.pm2 || []).filter((p) => p.online);
  const offlinePm2 = (metrics?.pm2 || []).filter((p) => !p.online);
  const sys = metrics?.system || {};

  return (
    <AppLayout>
      <div className="page-content">
        <div style={{ marginBottom: '1.5rem' }}>
          <Link to="/servers" style={{ fontSize: 13, color: '#64748b', textDecoration: 'none', fontWeight: 600 }}>
            ← Back to servers
          </Link>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0f0f1a', letterSpacing: '-0.03em', margin: 0 }}>
              {serverMeta?.name || serverId}
            </h1>
            {serverMeta && <StatusBadge status={serverMeta.status} />}
            {metrics?.uptime && (
              <span style={{ fontSize: 13, color: '#64748b' }}>up {metrics.uptime}</span>
            )}
          </div>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 6 }}>
            Private monitor via ABGA Dashboard — no public monitor URL
          </p>
        </div>

        {error && (
          <div style={{
            padding: '0.85rem 1rem', borderRadius: 12, marginBottom: '1.25rem',
            background: '#fef2f2', color: '#b91c1c', borderLeft: '3px solid #b91c1c', fontSize: 14,
          }}>
            {error}
          </div>
        )}

        {loading && !metrics && !error && (
          <p style={{ color: '#64748b' }}>Loading metrics…</p>
        )}

        {metrics && (
          <>
            {metrics.alerts?.length > 0 && (
              <div style={{
                padding: '0.85rem 1rem', borderRadius: 12, marginBottom: '1.25rem',
                background: '#fef2f2', color: '#b91c1c', fontSize: 14,
              }}>
                Alerts: {metrics.alerts.join(', ')}
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              marginBottom: '1.5rem',
            }}>
              <MetricCard label="CPU" value={`${metrics.cpu.percent}%`} sub={`${metrics.cpu.cores} cores`} />
              <MetricCard label="Memory" value={`${metrics.memory.percent}%`} sub={`${metrics.memory.used} / ${metrics.memory.total} GB`} />
              <MetricCard label="Disk" value={`${metrics.disk.root.percent}%`} sub={`${metrics.disk.root.used} / ${metrics.disk.root.total} GB`} />
              <MetricCard
                label="/tmp"
                value={
                  metrics.disk.tmp
                    ? (metrics.disk.tmp.dir_gb != null
                      ? `${metrics.disk.tmp.dir_gb} GB`
                      : `${metrics.disk.tmp.percent}%`)
                    : '—'
                }
                sub={
                  metrics.disk.tmp
                    ? (metrics.disk.tmp.same_as_root
                      ? `${metrics.disk.tmp.percent}% of disk · ${metrics.disk.tmp.note || 'on root'}`
                      : `${metrics.disk.tmp.used} / ${metrics.disk.tmp.total} GB`)
                    : 'n/a'
                }
              />
              <MetricCard
                label="Requests"
                value={metrics.requests.available ? metrics.requests.per_min : '—'}
                sub={metrics.requests.available ? 'req/min' : metrics.requests.note}
              />
              <MetricCard
                label="Bandwidth"
                value={`${metrics.network.recv_mb_s} / ${metrics.network.sent_mb_s}`}
                sub="in / out MB/s"
              />
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 12,
              marginBottom: '1.75rem',
            }}>
              <ChartCard title="CPU">
                <Sparkline data={history.cpu} color="#0f766e" max={100} unit="%" />
              </ChartCard>
              <ChartCard title="Memory">
                <Sparkline data={history.mem} color="#b45309" max={100} unit="%" />
              </ChartCard>
              <ChartCard title="Disk">
                <Sparkline data={history.disk} color="#0e7490" max={100} unit="%" />
              </ChartCard>
              <ChartCard title="/tmp size">
                <Sparkline
                  data={history.tmp}
                  color="#a16207"
                  max={metrics.disk.tmp?.same_as_root ? 100 : undefined}
                  unit={metrics.disk.tmp?.same_as_root ? '% of disk' : 'GB'}
                  digits={metrics.disk.tmp?.same_as_root ? 2 : 3}
                />
              </ChartCard>
              <ChartCard title="Bandwidth in">
                <Sparkline data={history.bwIn} color="#0f766e" unit="MB/s" digits={3} />
              </ChartCard>
              <ChartCard title="Bandwidth out">
                <Sparkline data={history.bwOut} color="#b45309" unit="MB/s" digits={3} />
              </ChartCard>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {[
                ['online', `Online (${metrics.pm2_summary?.online ?? onlinePm2.length})`],
                ['offline', `Offline (${metrics.pm2_summary?.offline ?? offlinePm2.length})`],
                ['system', 'System info'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  style={{
                    font: 'inherit', fontSize: 13, fontWeight: 600, padding: '8px 14px', borderRadius: 10,
                    border: tab === id ? '1px solid #cbd5e1' : '1px solid transparent',
                    background: tab === id ? '#fff' : 'transparent',
                    color: tab === id ? '#0f0f1a' : '#64748b', cursor: 'pointer',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'online' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
                {onlinePm2.length ? onlinePm2.map((p) => (
                  <ServiceRow
                    key={p.name}
                    name={p.name}
                    online
                    detail={`${p.cpu}% cpu · ${p.memory_mb} MB · ${p.restarts} restarts${p.uptime_ms ? ` · up ${fmtUptime(p.uptime_ms)}` : ''}`}
                  />
                )) : <p style={{ color: '#64748b', fontSize: 14 }}>No online PM2 processes</p>}
              </div>
            )}

            {tab === 'offline' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
                {offlinePm2.length ? offlinePm2.map((p) => (
                  <ServiceRow key={p.name} name={p.name} online={false} detail={p.status} />
                )) : <p style={{ color: '#64748b', fontSize: 14 }}>All PM2 processes are online</p>}
              </div>
            )}

            {tab === 'system' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginBottom: 16 }}>
                  <InfoRow label="Hostname" value={sys.hostname} />
                  <InfoRow label="OS" value={sys.os_pretty} />
                  <InfoRow label="Kernel" value={sys.kernel} />
                  <InfoRow label="Last boot" value={sys.boot_at} />
                  <InfoRow label="Running since" value={sys.boot_since || metrics.uptime} />
                  <InfoRow label="Load avg" value={sys.load_1m != null ? `${sys.load_1m} · ${sys.load_5m} · ${sys.load_15m}` : '—'} />
                  <InfoRow label="Memory" value={`${metrics.memory.used} / ${metrics.memory.total} GB`} />
                  <InfoRow label="Bandwidth totals" value={`${metrics.network.bytes_recv_mb} MB in · ${metrics.network.bytes_sent_mb} MB out`} />
                </div>
                <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Infrastructure</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                  {Object.entries(metrics.infra || {}).map(([name, ok]) => (
                    <ServiceRow key={name} name={name} online={ok} detail="systemctl" />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
